// work srcctl — «Контроль исходников» (роль `sources`, Глеб 2026-09-11).
//
// Отдельный кабинет: дашборд-пилюли локаль × типаж (как «Лимиты» у менеджера),
// клик по пилюле открывает модалку ячейки — список заведённых исходников и
// форма заведения нового. Заводящий выбирает АГРЕГАТОР или МЕРЧАНТ, даёт имя,
// прикладывает информацию для размножения (свободный JSON) и — для агрегатора
// — квоту, которая по умолчанию проставляется сама и руками только понижается.
//
// Пишет и читает тот же реестр `design_sources`, что дизайнерский флоу: это
// ОДИН каталог ячеек, второго быть не должно (мигр. 0085 добавила две колонки).
import { z } from 'zod';

/**
 * Предел на ОДИН агрегатор в ОДНОМ коде локали, МЕЖ ВСЕХ КАТЕГОРИЙ (правило
 * владельца 2026-09-11): «не больше 450 Groupon меж всех категорий es_ES».
 * Считается по `capMerchantKey` имени — иначе «Groupon» и «groupon ES» ушли бы
 * двумя квотами по 450.
 */
export const SRC_AGG_LOCALE_CAP = 450;

/** Категории каталога, по которым режется дашборд (колонки пилюль). */
export const SRC_CATEGORIES = ['membership', 'coupon', 'gift_card', 'event_ticket'] as const;
export type SrcCategory = (typeof SRC_CATEGORIES)[number];

/**
 * Категория ячейки по её `kind`. В реестре живут оба написания гифт-карты
 * ('giftcard' из Dev-кабинета и 'gift_card' из каталога) и типажи с
 * модальностью ('giftcard_screenshot') — сводим к одной категории здесь, чтобы
 * пилюля не двоилась.
 */
export function srcCategoryOf(kind: string): SrcCategory {
  const k = String(kind || '').toLowerCase();
  if (k.includes('giftcard') || k.includes('gift_card')) return 'gift_card';
  if (k.includes('coupon')) return 'coupon';
  if (k.includes('ticket')) return 'event_ticket';
  return 'membership';
}

/** Строка реестра глазами кабинета «Контроль исходников». */
export const SrcCtlSourceSchema = z.object({
  locale: z.string(),
  merchant_name: z.string(),
  kind: z.string(),
  /** '' = легаси (тип выводится из merchants[]), иначе merchant | aggregator. */
  source_type: z.string().default(''),
  merchants: z.array(z.string()).default([]),
  /** draft (исходника ещё нет) | pending_eval | ready | no_code | eval_failed */
  status: z.string().default(''),
  eval_status: z.string().default(''),
  note: z.string().default(''),
  /** Информация для размножения — как её ввели (валидный JSON или ''). */
  repl_json: z.string().default(''),
  /** Квота агрегатора в локали (0 = единичный мерчант: кап мерчанта). */
  agg_quota: z.number().int().default(0),
  /** Сдано дизайнером по ячейке (не отклонённое) — «сколько квоты выбрано». */
  delivered: z.number().int().default(0),
  /** Кадров ячейки, отклонённых QA/eval и ждущих перерисовки (> 0 ⇒ ячейка красная). */
  rework: z.number().int().default(0),
  /** ЗА ЧТО вернули — уникальные причины QA (≤5). Красное без причины
   *  нечитаемо: человек видит рамку и не знает, что чинить. */
  rework_reasons: z.array(z.string()).default([]),
  /** Подписанный URL файла исходника для <img> ('' = файла нет).
   *  Подпись, а не Firebase-токен: у тега <img> заголовков нет (sign.ts). */
  thumb_url: z.string().default(''),
  /** Последняя версия мастера (0 = дизайнер ещё не рисовал). */
  master_version: z.number().int().default(0),
  master_ready: z.boolean().default(false),
  /** В какой подкатегории завели: 'screenshot' | 'photo' ('' = легаси → скриншот). */
  modality: z.string().default(''),
  /** ЗАВЕДЁН РУКАМИ (кабинет, старый Dev-кабинет, плагин дизайнера). Всё
   *  остальное — скрейперы, переносы, импорты — не «добавлено», а «на
   *  оценку»: потенциальный пример, который человек ещё не подтвердил
   *  (владелец, 2026-09-12). */
  manual: z.boolean().default(true),
  /** Откуда взялась строка одним словом: кабинет | плагин | скрейпер |
   *  перенос | импорт. Для подписи на карточке «на оценку». */
  origin_kind: z.string().default(''),
  /**
   * КТО завёл исходник: имя из профиля, при его отсутствии — часть почты до
   * «@». Пусто у автоматов (скрейпер, перенос, импорт) — там автора нет.
   * Почта целиком на экран не уходит.
   */
  author: z.string().default(''),
  /** Вердикт кабинета по «потенциальному примеру»: '' — ещё на оценке,
   *  'confirmed' — взято в работу (уехало в «Добавлено»), 'rejected' — не
   *  подходит (ушло из активного поля, причина видна по клику). */
  assess: z.string().default(''),
  /** Причина отказа при оценке ('' = отказа не было). */
  assess_reason: z.string().default(''),
  /** Сколько ещё исходников есть у этой ячейки помимо основного (мигр. 0074). */
  variants: z.number().int().default(0),
  /** Оригинал ПРОШЁЛ приёмку: декод нашёл код, eval разобрал поля, мастер
   *  отрисован (`status='ready'`). С этого момента ячейка видна дизайнеру в
   *  Figma-плагине, а в кабинете помечается галочкой. */
  eval_ok: z.boolean().default(false),
  has_image: z.boolean().default(false),
  /** Файл исходника приложен (картинка ИЛИ pdf, мигр. 0087). */
  has_file: z.boolean().default(false),
  /** 'image' | 'pdf' | '' — чем именно приложен исходник. */
  file_kind: z.string().default(''),
  /** У PDF есть растровая обложка (мигр. 0093): показываем её, а не заглушку. */
  has_preview: z.boolean().default(false),
  /**
   * Строка ждёт ответа ИМЕННО ЭТОГО пользователя — по ней он видит пульсацию
   * и два действия. Обычному сотруднику это его собственные строки: чужую
   * недоделку он всё равно не починит. Владельцу — ВСЕ (2026-09-14): он
   * следит, чтобы ни одна не зависла неразобранной.
   */
  mine: z.boolean().default(false),
  /** Ответ «PDF у меня нет» (мигр. 0095): пульсация гаснет, строка остаётся
   *  на доп. проверке. */
  no_pdf: z.boolean().default(false),
  /**
   * Подписанная ссылка на ОРИГИНАЛ файла (для скачивания). У PDF `thumb_url`
   * ведёт на растровую обложку, а сюда — на сам лист: дизайнер работает с
   * вектором, а не с картинкой.
   */
  file_url: z.string().default(''),
  /** Откуда взят исходник. Пусто ⇒ ячейка «не доделана». */
  source_url: z.string().default(''),
  created_at: z.string().default(''),
});
export type SrcCtlSource = z.infer<typeof SrcCtlSourceSchema>;

/**
 * ЗАПОЛНЕН ли исходник — уточнено владельцем 2026-09-12: имя + ОРИГИНАЛ
 * (png/jpeg/pdf). Ссылка осталась полем, но критерием быть перестала:
 * «заполненным считаем то, что заведено с каким-то оригиналом».
 * Одна функция на воркер и на кабинет: два определения «готово» разошлись бы.
 */
export function srcFilled(s: { merchant_name: string; has_file: boolean }): boolean {
  return !!s.merchant_name.trim() && s.has_file;
}

/** Тип приложенного файла по content-type ('' = не поддерживаем). */
export function srcFileKind(contentType: string): 'image' | 'pdf' | '' {
  const t = String(contentType || '').toLowerCase();
  if (t === 'application/pdf') return 'pdf';
  if (t === 'image/png' || t === 'image/jpeg' || t === 'image/webp') return 'image';
  return '';
}

const Locale = z.string().trim().regex(/^[a-z]{2}_[A-Z]{2}$/, 'локаль вида es_ES');
const Merchant = z.string().trim().min(1).max(160);
/** Информация для размножения: свободный JSON текстом. '' = не заполнена. */
const ReplJson = z.string().max(200_000).default('');

export const SrcCtlCreateReqSchema = z.object({
  locale: Locale,
  merchant_name: Merchant,
  /** Категория ячейки: membership | coupon | gift_card | event_ticket. */
  kind: z.enum(SRC_CATEGORIES),
  source_type: z.enum(['merchant', 'aggregator']),
  /** Подкатегория, из которой заводят: скриншот или фото (мигр. 0090).
   *  Литерал, а не `SRC_MODALITIES`: тот объявлен ниже по файлу, и ссылка на
   *  него отсюда падает в TDZ прямо при импорте модуля. */
  modality: z.enum(['screenshot', 'photo']).default('screenshot'),
  /** Мерчанты агрегатора (можно пусто: их досеет AI-скан исходника). */
  merchants: z.array(z.string().trim().min(1).max(160)).max(200).default([]),
  repl_json: ReplJson,
  /** Желаемая квота агрегатора. 0 / больше свободного ⇒ сервер поставит своё. */
  agg_quota: z.number().int().min(0).max(SRC_AGG_LOCALE_CAP).default(0),
  note: z.string().trim().max(500).default(''),
  /** Ссылка на источник: откуда взяли. Без неё ячейка «не доделана». */
  source_url: z.string().trim().max(500).default(''),
  /** Файл исходника — png/jpeg/webp ИЛИ pdf (лист A4). Необязателен: ячейку
   *  можно завести раньше файла (статус draft) — контроль идёт впереди файла. */
  image_b64: z.string().max(12_000_000).default(''),
  content_type: z.string().trim().max(60).default('image/png'),
  /** ОБЛОЖКА PDF: PNG первой страницы, отрисованный браузером при заведении
   *  (у воркера растеризатора нет). Нужна, чтобы PDF было видно миниатюрой и
   *  чтобы он прошёл приёмку. В работу дизайнеру обложка НЕ идёт — мастер по
   *  ней не рендерится, вектор остаётся исходным листом. */
  preview_b64: z.string().max(12_000_000).default(''),
});
export type SrcCtlCreateReq = z.infer<typeof SrcCtlCreateReqSchema>;

export const SrcCtlCreateResSchema = z.object({
  ok: z.boolean(),
  existing: z.boolean().default(false),
  status: z.string().default(''),
  agg_quota: z.number().int().default(0),
  /** Квоту урезали до свободного остатка по агрегатору в локали. */
  quota_clamped: z.boolean().default(false),
});
export type SrcCtlCreateRes = z.infer<typeof SrcCtlCreateResSchema>;

/** Правка заведённой ячейки: JSON размножения, квота (только вниз), заметка. */
export const SrcCtlUpdateReqSchema = z.object({
  locale: Locale,
  merchant_name: Merchant,
  repl_json: ReplJson.optional(),
  agg_quota: z.number().int().min(0).max(SRC_AGG_LOCALE_CAP).optional(),
  note: z.string().trim().max(500).optional(),
  merchants: z.array(z.string().trim().min(1).max(160)).max(200).optional(),
  source_url: z.string().trim().max(500).optional(),
  /** Досыл файла в ячейку, заведённую без него (картинка → pending_eval). */
  image_b64: z.string().max(12_000_000).default(''),
  content_type: z.string().trim().max(60).default('image/png'),
  /** Обложка PDF — см. `SrcCtlCreateReqSchema.preview_b64`. */
  preview_b64: z.string().max(12_000_000).default(''),
});
export type SrcCtlUpdateReq = z.infer<typeof SrcCtlUpdateReqSchema>;

/** «PDF у меня нет» — ответ на пульсирующую строку доп. проверки. */
export const SrcCtlNoPdfReqSchema = z.object({
  locale: Locale,
  merchant_name: Merchant,
  /** false — снять отметку (передумал, PDF всё-таки будет). */
  no_pdf: z.boolean().default(true),
});
export type SrcCtlNoPdfReq = z.infer<typeof SrcCtlNoPdfReqSchema>;

export const SrcCtlNoPdfResSchema = z.object({ ok: z.boolean(), no_pdf: z.boolean().default(false) });
export type SrcCtlNoPdfRes = z.infer<typeof SrcCtlNoPdfResSchema>;

// ─── проверка мерчантов по реестру капов ────────────────────────────────────
// Заводящий добавляет мерчантов агрегатора по одному; каждый сразу сверяется с
// общим реестром (ADR-0059): занят целиком — красный и не берём, есть место —
// показываем, сколько ещё влезет из 20.
export const SrcCtlMerchantCheckReqSchema = z.object({
  locale: z.string().trim().min(2).max(10),
  merchants: z.array(z.string().trim().min(1).max(160)).min(1).max(50),
});
export type SrcCtlMerchantCheckReq = z.infer<typeof SrcCtlMerchantCheckReqSchema>;

export const SrcCtlMerchantCheckResSchema = z.object({
  ok: z.boolean(),
  cap: z.number().int().default(20),
  rows: z.array(z.object({
    merchant: z.string(),
    /** Сколько уже занято по реестру (soft + hard). */
    used: z.number().int().default(0),
    /** Сколько ещё можно (cap − used, но не меньше нуля). */
    left: z.number().int().default(0),
    /** Ячейка закрыта: мерчанта в этой локали брать нельзя. */
    closed: z.boolean().default(false),
  })).default([]),
});
export type SrcCtlMerchantCheckRes = z.infer<typeof SrcCtlMerchantCheckResSchema>;

/** Убрать исходник из кабинета: строка остаётся, помечается (мигр. 0088). */
export const SrcCtlRemoveReqSchema = z.object({
  locale: Locale,
  merchant_name: Merchant,
  /** false — вернуть обратно. */
  removed: z.boolean().default(true),
});
export type SrcCtlRemoveReq = z.infer<typeof SrcCtlRemoveReqSchema>;

// ─── оценка потенциального примера ──────────────────────────────────────────
export const SrcCtlAssessReqSchema = z.object({
  locale: Locale,
  merchant_name: Merchant,
  /** confirm — в работу; reject — не подходит (нужна причина); defer — оставить на оценке. */
  action: z.enum(['confirm', 'reject', 'defer']),
  reason: z.string().trim().max(500).default(''),
  /** Какой исходник ячейки основной ('' = основной сам, иначе id варианта). */
  main_variant: z.string().trim().max(64).default(''),
});
export type SrcCtlAssessReq = z.infer<typeof SrcCtlAssessReqSchema>;

export const SrcCtlAssessResSchema = z.object({ ok: z.boolean(), assess: z.string().default('') });
export type SrcCtlAssessRes = z.infer<typeof SrcCtlAssessResSchema>;

/** Все исходники ячейки: основной + варианты (мигр. 0074), с подписанными url. */
export const SrcCtlVariantsResSchema = z.object({
  ok: z.boolean(),
  main_variant: z.string().default(''),
  items: z.array(z.object({
    /** '' = основной исходник ячейки. */
    id: z.string(),
    sub_merchant: z.string().default(''),
    status: z.string().default(''),
    url: z.string().default(''),
    pdf: z.boolean().default(false),
  })).default([]),
  /** Мерчанты, заведённые внутри площадки. */
  merchants: z.array(z.string()).default([]),
});
export type SrcCtlVariantsRes = z.infer<typeof SrcCtlVariantsResSchema>;

// ─── кандидаты в мерчанты (выгрузка roadmap-агентов, мигр. 0091) ────────────
// СХЕМЫ ГОТОВЫ, ИМПОРТ ЕЩЁ НЕ ВКЛЮЧЁН: владелец остановил заведение до
// разбора картинок (декод показал, что код есть на 1,2% — это референсы, а не
// носители). Мигр. 0091 в проде не применена; включаем по отдельному слову.
export const SrcCtlCandidateSchema = z.object({
  id: z.string(),
  locale: z.string(),
  category: z.string(),
  modality: z.string(),
  merchant_name: z.string(),
  name_local: z.string().default(''),
  programme: z.string().default(''),
  how_to_get: z.string().default(''),
  url: z.string().default(''),
  /** ЧУЖАЯ картинка с сайта бренда — референс «как выглядит носитель», а не
   *  наш ассет. Показываем ссылкой и никогда не тянем в R2. */
  image_url: z.string().default(''),
  layer: z.string().default(''),
  confidence: z.string().default(''),
  reg_status: z.string().default(''),
  reg_collected: z.number().int().default(0),
  verdict: z.string().default(''),
  verdict_why: z.string().default(''),
});
export type SrcCtlCandidate = z.infer<typeof SrcCtlCandidateSchema>;

export const SrcCtlCandidatesResSchema = z.object({
  ok: z.boolean(),
  items: z.array(SrcCtlCandidateSchema).default([]),
  total: z.number().int().default(0),
});
export type SrcCtlCandidatesRes = z.infer<typeof SrcCtlCandidatesResSchema>;

/** Вердикт по кандидату: взяли в работу или не подходит (с причиной). */
export const SrcCtlCandidateVerdictReqSchema = z.object({
  id: z.string().trim().min(1),
  verdict: z.enum(['', 'taken', 'rejected']),
  reason: z.string().trim().max(500).default(''),
});
export type SrcCtlCandidateVerdictReq = z.infer<typeof SrcCtlCandidateVerdictReqSchema>;

/** Импорт выгрузки (админ): строки идут чанками, ключ — id ячейки кандидата. */
export const SrcCtlCandidateImportReqSchema = z.object({
  replace: z.boolean().default(false),
  items: z.array(z.object({
    locale: z.string().trim().min(2).max(10),
    category: z.enum(SRC_CATEGORIES),
    modality: z.enum(['screenshot', 'photo']),
    merchant_name: z.string().trim().min(1).max(160),
    merchant_key: z.string().trim().min(1).max(160),
    name_local: z.string().trim().max(200).default(''),
    programme: z.string().trim().max(300).default(''),
    how_to_get: z.string().trim().max(500).default(''),
    url: z.string().trim().max(500).default(''),
    image_url: z.string().trim().max(1000).default(''),
    layer: z.string().trim().max(40).default(''),
    confidence: z.string().trim().max(40).default(''),
    verify: z.string().trim().max(40).default(''),
    reg_status: z.string().trim().max(40).default(''),
    reg_collected: z.number().int().min(0).max(100000).default(0),
  })).max(500).default([]),
});
export type SrcCtlCandidateImportReq = z.infer<typeof SrcCtlCandidateImportReqSchema>;

export const SrcCtlCandidateImportResSchema = z.object({
  ok: z.boolean(), items: z.number().int().default(0), removed: z.number().int().default(0),
});
export type SrcCtlCandidateImportRes = z.infer<typeof SrcCtlCandidateImportResSchema>;

// ─── «Сообщить о проблеме» (мигр. 0092) ─────────────────────────────────────
export const SRC_TICKET_KINDS = [
  'данные врут',
  'не получается добавить',
  'вопрос по приоритетам',
  'ошибка интерфейса',
  'другое',
] as const;

export const SrcCtlTicketReqSchema = z.object({
  kind: z.string().trim().max(60).default('другое'),
  subject: z.string().trim().min(3, 'опишите одной строкой').max(200),
  body: z.string().trim().max(4000).default(''),
  context: z.string().trim().max(200).default(''),
  /** Файл не обязателен: скрин, PDF или что угодно, что помогает понять. */
  file_b64: z.string().max(12_000_000).default(''),
  file_name: z.string().trim().max(160).default(''),
  content_type: z.string().trim().max(80).default(''),
});
export type SrcCtlTicketReq = z.infer<typeof SrcCtlTicketReqSchema>;

export const SrcCtlTicketSchema = z.object({
  id: z.string(),
  kind: z.string().default(''),
  subject: z.string().default(''),
  body: z.string().default(''),
  context: z.string().default(''),
  file_name: z.string().default(''),
  status: z.string().default('open'),
  created_at: z.string().default(''),
});
export type SrcCtlTicket = z.infer<typeof SrcCtlTicketSchema>;

export const SrcCtlTicketsResSchema = z.object({
  ok: z.boolean(),
  items: z.array(SrcCtlTicketSchema).default([]),
});
export type SrcCtlTicketsRes = z.infer<typeof SrcCtlTicketsResSchema>;

export const SrcCtlRemoveResSchema = z.object({ ok: z.boolean(), removed: z.boolean().default(true) });
export type SrcCtlRemoveRes = z.infer<typeof SrcCtlRemoveResSchema>;

export const SrcCtlUpdateResSchema = z.object({
  ok: z.boolean(),
  agg_quota: z.number().int().default(0),
  quota_clamped: z.boolean().default(false),
  /** Исходник досланы этим вызовом — ячейка ушла в pending_eval. */
  status: z.string().default(''),
});
export type SrcCtlUpdateRes = z.infer<typeof SrcCtlUpdateResSchema>;


// ─── Второй заход (2026-09-11): лимиты, приоритеты, overflow ────────────────
// Дашборд режется на ПОДКАТЕГОРИИ: локаль × типаж × модальность — ровно так
// считаются и капы плана, и цели заказчика. Одна пилюля = одна подкатегория.

/**
 * Откуда пришёл исходник — по `design_sources.origin`.
 *
 * РУЧНЫЕ (`manual`) — те, что завёл человек: кабинет контроля исходников,
 * старый Dev-кабинет и плагин дизайнера. Остальные (копилка скрейперов,
 * перенос из pothos, разовые импорты) человек не подтверждал — они идут
 * отдельной секцией «На оценку» и в «Добавлено» не считаются.
 */
export function srcOriginKind(origin: string): {
  kind: 'кабинет' | 'плагин' | 'скрейпер' | 'перенос' | 'импорт'; manual: boolean;
} {
  const o = String(origin || '');
  if (o.startsWith('srcctl:') || o.startsWith('dev-cabinet:')) return { kind: 'кабинет', manual: true };
  if (o.startsWith('plugin:')) return { kind: 'плагин', manual: true };
  if (o.startsWith('ext:')) return { kind: 'скрейпер', manual: false };
  if (o.startsWith('pothos:')) return { kind: 'перенос', manual: false };
  return { kind: 'импорт', manual: false };
}

/**
 * НА ДОП. ПРОВЕРКЕ (владелец, 2026-09-14).
 *
 * В фото-ячейке исходником считается только печатный оригинал — практически
 * всегда PDF. Снимок витрины или скриншот, положенный в фото, исходником не
 * является: с него нечего печатать. Такая строка из кабинета не исчезает, но
 * висит отдельно и НИ НА ОДИН счётчик не влияет — ни на главный, ни на
 * «Заполнено», ни на потенциал, ни на статистику загрузок.
 *
 * Скриншотные ячейки правило не трогает: там картинка и есть оригинал.
 */
export function srcHeld(s: { modality: string; file_kind: string; has_file: boolean }): boolean {
  return s.has_file && s.modality === 'photo' && s.file_kind !== 'pdf';
}

export const SRC_MODALITIES = ['screenshot', 'photo'] as const;
export type SrcModality = (typeof SRC_MODALITIES)[number];

/** Целевая цифра общего приоритета: 400 отгруженного и принятого. */
export const SRC_PRIO_TARGET = 400;

/**
 * ДОБОР С НОВЫХ ПЛОЩАДОК (владелец, 2026-09-12). Сверх основного капа
 * подкатегории можно взять ещё до 100 ассетов с НОВЫХ источников —
 * предложенных или добавленных, — но только если таких разных площадок или
 * мерчантов набралось минимум четыре. Одна площадка добора не открывает:
 * смысл в разнообразии, а не в том, чтобы долить сотню одним брендом.
 */
export const SRC_NEW_BONUS = 100;
export const SRC_NEW_MIN_SOURCES = 4;

/**
 * ДИВЕРСИФИКАЦИЯ ЯЧЕЙКИ (владелец, 2026-09-14). Главная цифра кабинета:
 * сколько исходников, ПРОШЕДШИХ eval (`status='ready'`), набрано по ячейкам
 * приоритетных локалей. Считаем не более четырёх с одной ячейки — пятый и
 * дальше в зачёт не идут: смысл в том, чтобы разные ячейки получили по
 * четыре разных источника, а не в том, чтобы одна набрала двадцать.
 */
export const SRC_DIVERSITY_PER_CELL = 4;

/**
 * Ячейка плана из снимка «Лимитов» weern-backend (`/api/_admin/limitsSnapshot`).
 * Считает ЕЁ там и только там (`computeLimitsRows`) — work берёт готовое.
 * `closed` = кап подкатегории в локали выбран: пилюля зелёная с галочкой.
 */
export const SrcCtlLimitCellSchema = z.object({
  locale: z.string(),
  category: z.string(),
  modality: z.string(),
  plan_target: z.number().int().default(0),
  hard_limit: z.number().int().default(0),
  shipped: z.number().int().default(0),
  total: z.number().int().default(0),
  remaining: z.number().int().default(0),
  closed: z.boolean().default(false),
});
export type SrcCtlLimitCell = z.infer<typeof SrcCtlLimitCellSchema>;

/** Общий приоритет: подкатегория с целью 400 (лист «Priorities»). */
export const SrcCtlPrioTargetSchema = z.object({
  locale: z.string(),
  category: z.string(),
  modality: z.string(),
  target: z.number().int().default(SRC_PRIO_TARGET),
  count_at_import: z.number().int().default(0),
  need_at_import: z.number().int().default(0),
  /** Умышленный overflow сверх капа — в ЕДИНИЦАХ площадок/мерчантов. >0 ⇒ пилюля фиолетовая. */
  overflow_units: z.number().int().default(0),
  overflow_note: z.string().default(''),
});
export type SrcCtlPrioTarget = z.infer<typeof SrcCtlPrioTargetSchema>;

/**
 * Приоритет-площадка (лист «TOP1 PRIO»): конкретный агрегатор, которого ждут
 * поимённо. ОТКЛОНИТЬ ПРОСТО ТАК НЕЛЬЗЯ — позиция активна, пока не описана
 * причина И не приложены ссылка и скриншот (правило владельца 2026-09-11).
 */
export const SrcCtlPrioMerchantSchema = z.object({
  id: z.string(),
  locale: z.string(),
  category: z.string(),
  merchant_name: z.string(),
  website: z.string().default(''),
  status: z.string().default(''),
  notes: z.string().default(''),
  min_amount: z.number().int().default(0),
  reject_reason: z.string().default(''),
  reject_url: z.string().default(''),
  /** Скриншот-доказательство лежит в R2. */
  has_reject_shot: z.boolean().default(false),
  /** Подписанный URL этого скриншота ('' = нет): открывается ссылкой, а у
   *  навигации заголовков нет — токен в адресе запрещён (sign.ts). */
  reject_shot_url: z.string().default(''),
  rejected_by: z.string().default(''),
  rejected_at: z.string().default(''),
  /** false ТОЛЬКО когда причина, ссылка и скриншот на месте все три. */
  active: z.boolean().default(true),
  /** Заведён ли уже исходник под эту площадку в этой ячейке. */
  has_source: z.boolean().default(false),
});
export type SrcCtlPrioMerchant = z.infer<typeof SrcCtlPrioMerchantSchema>;

/** Отказ по приоритет-площадке: все три доказательства обязательны. */
export const SrcCtlPrioRejectReqSchema = z.object({
  id: z.string().trim().min(1),
  reason: z.string().trim().min(10, 'причина: минимум 10 символов'),
  url: z.string().trim().url('нужна ссылка на источник причины'),
  image_b64: z.string().min(64, 'нужен скриншот причины'),
  content_type: z.string().trim().max(60).default('image/png'),
});
export type SrcCtlPrioRejectReq = z.infer<typeof SrcCtlPrioRejectReqSchema>;

/** Вернуть отклонённую площадку в работу (ошиблись причиной). */
export const SrcCtlPrioReopenReqSchema = z.object({ id: z.string().trim().min(1) });
export type SrcCtlPrioReopenReq = z.infer<typeof SrcCtlPrioReopenReqSchema>;

export const SrcCtlPrioRejectResSchema = z.object({ ok: z.boolean(), active: z.boolean().default(false) });
export type SrcCtlPrioRejectRes = z.infer<typeof SrcCtlPrioRejectResSchema>;

// ─── Импорт приоритетов (админ) ─────────────────────────────────────────────
// Обе вариации одним вызовом: цели и площадки. `replace` — полная замена
// набора (лист заказчика перевыгрузили целиком); без него — апсерт.
// Отказы при апсерте НЕ стираются: доказательство, добытое человеком, не
// должно пропадать от повторного импорта той же строки.
export const SrcCtlImportReqSchema = z.object({
  replace: z.boolean().default(false),
  targets: z.array(z.object({
    locale: z.string().trim().min(2).max(10),
    category: z.enum(SRC_CATEGORIES),
    modality: z.enum(SRC_MODALITIES),
    target: z.number().int().min(0).max(100000).default(SRC_PRIO_TARGET),
    count_at_import: z.number().int().default(0),
    need_at_import: z.number().int().default(0),
    overflow_units: z.number().int().min(0).max(100000).default(0),
    overflow_note: z.string().trim().max(500).default(''),
  })).max(2000).default([]),
  merchants: z.array(z.object({
    locale: z.string().trim().min(2).max(10),
    category: z.enum(SRC_CATEGORIES),
    merchant_name: z.string().trim().min(1).max(160),
    website: z.string().trim().max(300).default(''),
    status: z.string().trim().max(120).default(''),
    notes: z.string().trim().max(1000).default(''),
    min_amount: z.number().int().min(0).max(100000).default(0),
  })).max(2000).default([]),
});
export type SrcCtlImportReq = z.infer<typeof SrcCtlImportReqSchema>;

export const SrcCtlImportResSchema = z.object({
  ok: z.boolean(),
  targets: z.number().int().default(0),
  merchants: z.number().int().default(0),
  removed: z.number().int().default(0),
});
export type SrcCtlImportRes = z.infer<typeof SrcCtlImportResSchema>;

// ─── Свод дашборда ──────────────────────────────────────────────────────────
// Объявлен ПОСЛЕ всех своих частей: `const` не поднимается, и ссылка на
// схему, объявленную ниже, упала бы в TDZ прямо при импорте модуля.
/**
 * СТАТИСТИКА ЗАГРУЗОК ПО ЛЮДЯМ (владелец, 2026-09-14). Считаем ровно то же,
 * что и главный счётчик: исходники, ПРОШЕДШИЕ приёмку (`status='ready'`).
 * Ограничения «не более четырёх с ячейки» здесь НЕТ: тот потолок про
 * разнообразие цели, а не про вклад человека — иначе двое, закрывшие одну
 * ячейку вдвоём, поделили бы между собой четыре штуки.
 */
export const SrcCtlUploaderSchema = z.object({
  /** Имя из профиля; если его нет — часть почты до «@». Почта целиком не уходит. */
  name: z.string().default(''),
  /** Сколько исходников этого человека прошло приёмку. */
  count: z.number().int().default(0),
  /** Когда он загрузил последний из них (ISO); фронт считает «сколько назад». */
  last_at: z.string().default(''),
  /** Из них агрегаторов (площадка со списком брендов внутри). */
  aggregators: z.number().int().default(0),
  /**
   * Отдельные мерчанты — исходники, заведённые площадкой самой по себе.
   * Бренды ВНУТРИ агрегаторов сюда НЕ идут (владелец, 2026-09-14): они уже
   * учтены самим агрегатором, и складывать их значило бы считать одну и ту же
   * работу дважды. Вместе с `aggregators` даёт ровно число исходников.
   */
  merchants: z.number().int().default(0),
});
export type SrcCtlUploader = z.infer<typeof SrcCtlUploaderSchema>;

/**
 * ПОТЕНЦИАЛ (владелец, 2026-09-14) — сколько ассетов мы ещё можем сделать из
 * ТОГО, ЧТО УЖЕ ЗАВЕДЕНО. Считается по двум капам сразу:
 *   • на мерчанта — сколько слотов его ячейки свободно (реестр `merchant_caps`,
 *     ADR-0059): у агрегатора складываем по всем названным брендам, у прямого
 *     мерчанта берём его один;
 *   • на подкатегорию — остаток плана локали из снимка «Лимитов». Сумма по
 *     мерчантам им срезается: больше, чем возьмёт план, мы не отгрузим.
 * Разрез по модальности обязателен: фото и скриншоты — разные планы.
 */
export const SrcCtlPotentialSchema = z.object({
  photo: z.number().int().default(0),
  screenshot: z.number().int().default(0),
  total: z.number().int().default(0),
});
export type SrcCtlPotential = z.infer<typeof SrcCtlPotentialSchema>;

/** Потенциал одной локали + её волна приоритета (мигр. 0094). */
export const SrcCtlLocalePotentialSchema = SrcCtlPotentialSchema.extend({
  locale: z.string().default(''),
  /** 'P1'…'P4'; пусто — локали нет в распределении заказчика. */
  tier: z.string().default(''),
});
export type SrcCtlLocalePotential = z.infer<typeof SrcCtlLocalePotentialSchema>;

/**
 * Сводка потенциала для владельца: по волнам, отдельной строкой исключения и
 * общий потенциал к отгрузке.
 *
 * ВСЕ цифры стоят за уже заведённым исходником (владелец, 2026-09-14) — это
 * не «сколько попросили», а «сколько сделаем из того, что есть». Исключения —
 * тот же источниковый счёт по ячейкам, где план подкатегории уже выбран, а
 * заказчик их всё равно просит: резать такую цифру остатком плана нечем.
 */
export const SrcCtlPotentialSummarySchema = z.object({
  by_locale: z.array(SrcCtlLocalePotentialSchema).default([]),
  tiers: z.array(SrcCtlPotentialSchema.extend({ tier: z.string().default('') })).default([]),
  exceptions: SrcCtlPotentialSchema.default({ photo: 0, screenshot: 0, total: 0 }),
  grand: SrcCtlPotentialSchema.default({ photo: 0, screenshot: 0, total: 0 }),
});
export type SrcCtlPotentialSummary = z.infer<typeof SrcCtlPotentialSummarySchema>;

export const SrcCtlOverviewResSchema = z.object({
  ok: z.boolean(),
  /** Предел агрегатора на локаль (450) — фронт не хардкодит его сам. */
  agg_cap: z.number().int().default(SRC_AGG_LOCALE_CAP),
  /** Кап единичного мерчанта (GIFT_MERCHANT_CAP, обычно 20). */
  merchant_cap: z.number().int().default(20),
  sources: z.array(SrcCtlSourceSchema).default([]),
  /** Ячейки плана из снимка «Лимитов»; пусто = снимок ещё не забирали. */
  limits: z.array(SrcCtlLimitCellSchema).default([]),
  /** Когда снимок забран (ISO). Пусто/старое — цифры показываем как есть, с датой. */
  limits_fetched_at: z.string().default(''),
  limits_error: z.string().default(''),
  targets: z.array(SrcCtlPrioTargetSchema).default([]),
  prio_merchants: z.array(SrcCtlPrioMerchantSchema).default([]),
  /** Кто сколько загрузил (только прошедшее приёмку), по убыванию. */
  uploaders: z.array(SrcCtlUploaderSchema).default([]),
  /** Сводка потенциала. Пусто у всех, кроме владельческих учёток. */
  potential: SrcCtlPotentialSummarySchema.nullable().default(null),
});
export type SrcCtlOverviewRes = z.infer<typeof SrcCtlOverviewResSchema>;
